/**
 * Leviathan Web - Receipts Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface ReceiptItem {
  id: string;
  receivedQuantity: number;
  unit: string;
  unitCost: number;
  lineTotal: number;
  product: {
    id: string;
    name: string;
    sku: string;
    unit: string;
  };
  tank?: {
    id: string;
    tankNumber: string;
    name: string;
  };
}

interface Receipt {
  id: string;
  receiptNumber: string;
  receiptDate: string;
  deliveryDate: string | null;
  status: string;
  supplierInvoice: string | null;
  bolNumber: string | null;
  subtotal: number;
  total: number;
  supplier: {
    id: string;
    name: string;
    supplierNumber: string;
  };
  items: ReceiptItem[];
}

interface Stats {
  total: number;
  pending: number;
  received: number;
  verified: number;
  postedThisMonth: number;
  totalReceivedQty: number;
  totalCostThisMonth: number;
  byStatus: {
    pending: number;
    received: number;
    verified: number;
    posted: number;
    disputed: number;
    cancelled: number;
  };
}

interface StatCardProps {
  label: string;
  value: number | string;
  status?: 'operational' | 'warning' | 'critical';
}

/**
 * Compact stat card for receipt metrics
 */
function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

/**
 * Get status color
 */
function getStatusColor(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'text-warning-amber';
    case 'RECEIVED':
      return 'text-steel';
    case 'VERIFIED':
      return 'text-success-green';
    case 'POSTED':
      return 'text-success-green';
    case 'DISPUTED':
      return 'text-error-red';
    case 'CANCELLED':
      return 'text-text-tertiary';
    default:
      return 'text-text-secondary';
  }
}

const ReceiptsPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [search, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/receipts/stats`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchReceipts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/receipts?${params}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReceipts(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <div className="space-y-6 relative">
      {/* Header with geometric frame */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Receipts
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Fuel Receiving & Inventory Management
              </p>
            </div>
            <Link
              to="/receipts/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Receipt</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Pending" value={stats.pending} status="warning" />
          <StatCard label="Received" value={stats.received} status="operational" />
          <StatCard label="Verified" value={stats.verified} />
          <StatCard label="Posted (MTD)" value={stats.postedThisMonth} status="operational" />
          <StatCard label="Cost (MTD)" value={formatCurrency(stats.totalCostThisMonth)} />
        </div>
      )}

      {/* Filters Panel */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Receipt #, BOL, Invoice..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all placeholder:text-text-tertiary"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="RECEIVED">Received</option>
              <option value="VERIFIED">Verified</option>
              <option value="POSTED">Posted</option>
              <option value="DISPUTED">Disputed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Receipts Table */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border clip-path-octagon overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono animate-pulse">
                &gt; Loading receipts...
              </div>
            </div>
          ) : receipts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-text-tertiary font-mono text-lg mb-2">
                &gt; No receipts found
              </div>
              <p className="text-text-tertiary font-mono text-sm">
                {search || statusFilter
                  ? 'Try adjusting your search filters'
                  : 'Create your first receipt to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-panel-border">
                  <tr>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Receipt
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Supplier
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Date
                    </th>
                    <th className="text-left px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Products
                    </th>
                    <th className="text-right px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Total
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Status
                    </th>
                    <th className="text-center px-6 py-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => (
                    <tr
                      key={receipt.id}
                      className="border-b border-panel-border hover:bg-amber/5 transition-colors cursor-pointer"
                      onClick={() => navigate(`/receipts/${receipt.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-amber font-mono font-semibold">
                            {receipt.receiptNumber}
                          </div>
                          {receipt.bolNumber && (
                            <div className="text-text-tertiary font-mono text-xs">
                              BOL: {receipt.bolNumber}
                            </div>
                          )}
                          {receipt.supplierInvoice && (
                            <div className="text-text-tertiary font-mono text-xs">
                              INV: {receipt.supplierInvoice}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-text-primary font-mono text-sm">
                            {receipt.supplier.name}
                          </div>
                          <div className="text-text-tertiary font-mono text-xs">
                            {receipt.supplier.supplierNumber}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-text-secondary font-mono text-sm">
                          {formatDate(receipt.receiptDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {receipt.items.slice(0, 2).map((item) => (
                            <div key={item.id} className="text-text-secondary font-mono text-xs">
                              {item.product.name}: {item.receivedQuantity} {item.unit}
                            </div>
                          ))}
                          {receipt.items.length > 2 && (
                            <div className="text-text-tertiary font-mono text-xs">
                              +{receipt.items.length - 2} more
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div
                          className="text-amber font-mono font-semibold"
                          style={{ textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' }}
                        >
                          {formatCurrency(receipt.total)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`${getStatusColor(receipt.status)} font-mono text-xs uppercase tracking-wider font-semibold`}
                          style={{ textShadow: '0 0 6px currentColor' }}
                        >
                          {receipt.status}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center space-x-2">
                          {receipt.status === 'POSTED' && (
                            <button
                              onClick={() => navigate(`/ap/new?supplierId=${receipt.supplier.id}&receiptId=${receipt.id}`)}
                              className="text-amber hover:text-amber/80 font-mono text-sm"
                              title="Create Bill"
                            >
                              📄
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/receipts/${receipt.id}/edit`)}
                            className="text-text-secondary hover:text-amber font-mono text-sm"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => navigate(`/receipts/${receipt.id}`)}
                            className="text-steel hover:text-amber font-mono text-sm"
                            title="View"
                          >
                            👁️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptsPage;
